/**
 * The ten events: order, day, units, qualifying standards and IAAF scoring coefficients
 * (men's 1985 combined-events tables, still current).
 */
export type EventKind = 'sprint' | 'hurdles' | 'jump' | 'vault' | 'throw' | 'distance';
export type EventId = '100m' | 'lj' | 'sp' | 'hj' | '400m' | '110h' | 'pv' | 'dt' | 'jt' | '800m';

export interface Scoring {
  a: number;
  b: number;
  c: number;
  /** Track events score on time in seconds; jumps on centimetres; throws on metres. */
  measure: 'time' | 'centimetres' | 'metres';
}

export interface EventDef {
  id: EventId;
  name: string;
  /** Short label for scoreboards and tables. */
  short: string;
  day: 1 | 2;
  kind: EventKind;
  unit: 'seconds' | 'metres';
  scoring: Scoring;
  /**
   * Qualifying standard for season 1: a maximum time or a minimum distance/height.
   * Miss it and you lose a life, like the original.
   */
  standard: number;
  /** A world-class mark for the results screen ("Daley did 10.44"). */
  worldClass: number;
  /** One-line rules card shown in the event intro. */
  blurb: string;
}

export const EVENTS: EventDef[] = [
  {
    id: '100m',
    name: '100 metres',
    short: '100m',
    day: 1,
    kind: 'sprint',
    unit: 'seconds',
    scoring: { a: 25.4347, b: 18, c: 1.81, measure: 'time' },
    standard: 12.0,
    worldClass: 10.44,
    blurb: 'Alternate the two run keys as fast as you can. Same key twice does nothing.',
  },
  {
    id: 'lj',
    name: 'Long jump',
    short: 'LJ',
    day: 1,
    kind: 'jump',
    unit: 'metres',
    scoring: { a: 0.14354, b: 220, c: 1.4, measure: 'centimetres' },
    standard: 6.0,
    worldClass: 8.01,
    blurb:
      'Run up, then hold Space before the board to set your angle and release to fly. Past the board is a foul.',
  },
  {
    id: 'sp',
    name: 'Shot put',
    short: 'SP',
    day: 1,
    kind: 'throw',
    unit: 'metres',
    scoring: { a: 51.39, b: 1.5, c: 1.05, measure: 'metres' },
    standard: 12.0,
    worldClass: 15.72,
    blurb: 'Mash to build power in the circle, hold Space for the angle, release to put.',
  },
  {
    id: 'hj',
    name: 'High jump',
    short: 'HJ',
    day: 1,
    kind: 'jump',
    unit: 'metres',
    scoring: { a: 0.8465, b: 75, c: 1.42, measure: 'centimetres' },
    standard: 1.8,
    worldClass: 2.03,
    blurb:
      'Pick a bar height, run up, and time Space for take-off and angle. Three misses ends the event.',
  },
  {
    id: '400m',
    name: '400 metres',
    short: '400m',
    day: 1,
    kind: 'sprint',
    unit: 'seconds',
    scoring: { a: 1.53775, b: 82, c: 1.81, measure: 'time' },
    standard: 55.0,
    worldClass: 46.97,
    blurb: 'One lap. Watch the stamina bar: hold a rhythm you can keep, then sprint the last 100.',
  },
  {
    id: '110h',
    name: '110 metres hurdles',
    short: '110H',
    day: 2,
    kind: 'hurdles',
    unit: 'seconds',
    scoring: { a: 5.74352, b: 28.5, c: 1.92, measure: 'time' },
    standard: 16.0,
    worldClass: 14.33,
    blurb: 'Run with the keys and tap Space to hurdle. Hitting a hurdle costs speed.',
  },
  {
    id: 'pv',
    name: 'Pole vault',
    short: 'PV',
    day: 2,
    kind: 'vault',
    unit: 'metres',
    scoring: { a: 0.2797, b: 100, c: 1.35, measure: 'centimetres' },
    standard: 4.0,
    worldClass: 5.0,
    blurb: 'Run up, press Space to plant at the box, press again at the top to release.',
  },
  {
    id: 'dt',
    name: 'Discus',
    short: 'DT',
    day: 2,
    kind: 'throw',
    unit: 'metres',
    scoring: { a: 12.91, b: 4, c: 1.1, measure: 'metres' },
    standard: 38.0,
    worldClass: 46.56,
    blurb:
      'Alternate to spin up, release with Space at the right moment. Too early or late is a sector foul.',
  },
  {
    id: 'jt',
    name: 'Javelin',
    short: 'JT',
    day: 2,
    kind: 'throw',
    unit: 'metres',
    scoring: { a: 10.14, b: 7, c: 1.08, measure: 'metres' },
    standard: 50.0,
    worldClass: 65.24,
    blurb: 'Run up, hold Space for the angle and release before the line.',
  },
  {
    id: '800m',
    name: '800 metres',
    short: '800m',
    day: 2,
    kind: 'distance',
    unit: 'seconds',
    // The decathlon's 1500m replaced by the 800m for pace; scored on the IAAF 800m
    // combined-events table (the heptathlon one, the only 800m table in the set).
    scoring: { a: 0.11193, b: 254, c: 1.88, measure: 'time' },
    standard: 160.0,
    worldClass: 105.0,
    blurb: 'Two laps. Pace yourself with the stamina bar and save a sprint finish.',
  },
];

export const DAY1: EventDef[] = EVENTS.filter((e) => e.day === 1);
export const DAY2: EventDef[] = EVENTS.filter((e) => e.day === 2);

export function eventById(id: string): EventDef {
  const ev = EVENTS.find((e) => e.id === id);
  if (!ev) throw new Error(`Unknown event: ${id}`);
  return ev;
}

/** Track events are lower-is-better. */
export function lowerIsBetter(ev: EventDef): boolean {
  return ev.unit === 'seconds';
}

/** Each season tightens every standard by ~8%: faster times, longer distances. */
export const SEASON_FACTOR = 0.08;
export function standardFor(ev: EventDef, season: number): number {
  const n = Math.max(0, season - 1);
  const f = lowerIsBetter(ev) ? (1 - SEASON_FACTOR) ** n : (1 + SEASON_FACTOR) ** n;
  return roundMark(ev, ev.standard * f);
}

export function meetsStandard(ev: EventDef, mark: number | null, season = 1): boolean {
  if (mark === null) return false;
  const std = standardFor(ev, season);
  return lowerIsBetter(ev) ? mark <= std : mark >= std;
}

/** Round to the precision the event is measured at: 0.01 s or 0.01 m. */
export function roundMark(_ev: EventDef, mark: number): number {
  return Math.round(mark * 100) / 100;
}

/** "10.44", "4:35.00", "8.01 m" */
export function formatMark(ev: EventDef, mark: number | null): string {
  if (mark === null) return ev.unit === 'seconds' ? 'DNF' : 'NM';
  if (ev.unit === 'metres') return `${mark.toFixed(2)} m`;
  if (mark >= 60) {
    const m = Math.floor(mark / 60);
    const s = mark - m * 60;
    return `${m}:${s.toFixed(2).padStart(5, '0')}`;
  }
  return mark.toFixed(2);
}

/** A single capability list shared by the server and menus. */
export const PLAYABLE = new Set<string>(EVENTS.map((e) => e.id));
export const PLAYABLE_PROGRAMMES = new Set<string>(['full', 'day1', 'day2']);
